import React, { useEffect, useState, useMemo, useRef } from "react";
import { ServiceEventNames, ServiceActionTypes, PaymentStatus, HorseGender, PaymentTypeMethods, PaymentRedirectUrls } from '../../shared/utils/ServicesConstants';
import { LeaseFieldNames } from '../utils/HorseLeasingConstants';
import { TranslationKeys } from '../../../../constants/TranslationKeys';
import { PaymentService } from '../../shared/utils/PaymentService';
import { UsersService } from '../../shared/utils/UsersService';
import { useForm, FormProvider } from "react-hook-form";
import { useTranslate } from "../../../../components/TranslationText";
import Loader from "../../../../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import dayjs from "dayjs";
import { toast, ToastContainer } from "react-toastify";
import HorseLeasingActionButtons from "../Components/HorseLeasingActionButtons";
import CustomPopUp from "../../../../components/CustomPopUp";

import HorseLeasingHorseSection from "../Components/HorseLeasingHorseSection";
import HorseLeasingCoveringSection from "../Components/HorseLeasingCoveringSection";
import HorseLeasingLesseeSection from "../Components/HorseLeasingLesseeSection";
import HorseLeasingLeaseDetailsSection from "../Components/HorseLeasingLeaseDetailsSection";
import HorseLeasingDocumentSection from "../Components/HorseLeasingDocumentSection";
import HorseLeasingCommentsSection from "../Components/HorseLeasingCommentsSection";
import HorseLeasingTermsSection from "../Components/HorseLeasingTermsSection";
import {
    serviceRequestHeadContainer,
    serviceRequestTitle,
    userDropDown
} from "../../../../common/cssclasses";
import { formatDateTimeToString } from "../../../../helper/formatDateTimeToString";
import {
    CreateOrEditHorseLeasethunk,
    ApproveRejectHorseLeasethunk
} from "../../../../Redux/HorseLeasing/horseLeasingAction";
import { useHorseLeasingData } from "../hooks/useHorseLeasingData";
import { yupResolver } from "@hookform/resolvers/yup";
import { getHorseLeasingSchema } from "../utils/HorseLeasingValidation";
import UserCentralDropDown from "../../../../components/shared/UserCentralDropDown";
import { DATE_FORMAT_HTML5 } from "../../../../constants/dates";

export default function HorseLeasingRequestPage() {
    const { translate } = useTranslate();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const _isApproved = useRef(null);
    const _isPayNow = useRef(true);

    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.has("id") ? searchParams.get("id") : null;
    const action = searchParams.has("action") ? searchParams.get("action") : null;
    const { currentLanguage } = useSelector((state) => state.studBookSlice);
    const currentUserId = localStorage.getItem("EAHSUserID");
    const userProfile = JSON.parse(localStorage.getItem("eahsUserProfile"));
    const [selectedOwnerId, setSelectedOwnerId] = useState(localStorage.getItem("currentSubAccountId") || currentUserId);
    const {
        HORSE_LEASING_SERVICE_ID,
        ownerHorsesDropDownData,
        isLoading,
        toasterMessage,
        clearToasterMessage,
        getDocumentTypesByServiceIdData,
        getServicesRequestByIdData,
        getCovCerByHorseIdData,
        getServiceDefinitionByIdData,
        fetchCoveringCertificate
    } = useHorseLeasingData(id, selectedOwnerId, currentLanguage, action);

    const methods = useForm({
        resolver: yupResolver(getHorseLeasingSchema(translate)),
        defaultValues: {
            [LeaseFieldNames.HORSE_ID]: "",
            [LeaseFieldNames.LESSEE_ID]: "",
            [LeaseFieldNames.LEASE_START_DATE]: "",
            [LeaseFieldNames.LEASE_END_DATE]: "",
            [LeaseFieldNames.CAN_PARTICIPATE_BEAUTY]: false,
            [LeaseFieldNames.CAN_COVER_REGISTER]: false,
            [LeaseFieldNames.CAN_REGISTER_OTHER]: false,
            [LeaseFieldNames.CAN_IMPORT_EXPORT]: false,
            [LeaseFieldNames.IS_MARE_COVERED]: false,
            [LeaseFieldNames.LESSEE_COMMENT]: "",
            [LeaseFieldNames.DOC_TYPE_DROP]: "",
            [LeaseFieldNames.ATTACHMENT_FILE]: null,
            [LeaseFieldNames.HORSE_ORIGIN]: "",
            [LeaseFieldNames.HORSE_BREEDER]: "",
            [LeaseFieldNames.HORSE_GENDER_DISPLAY]: "",
            [LeaseFieldNames.HORSE_GENDER]: "",
            [LeaseFieldNames.HORSE_COLOR]: "",
            [LeaseFieldNames.HORSE_BIRTH_DATE]: "",
            [LeaseFieldNames.HORSE_SIRE]: "",
            [LeaseFieldNames.HORSE_DAM]: "",
            [LeaseFieldNames.COVERING_CERT_NO]: "",
            [LeaseFieldNames.STALLION_NAME]: "",
            [LeaseFieldNames.COVERING_DATE]: ""
        }
    });

    const [timeoutId, setTimeoutId] = useState(0);
    const [openPopupReject, setOpenPopupReject] = useState(false);

    // Status state
    const [eventName, setEventName] = useState("");
    const hasInitialized = useRef(false);
    const canParticipateWithoutPayment = userProfile?.participateWithoutPayment === true;

    // Initial Load (Edit / View)

    // Handle fetched data for Edit/View
    const leaseRequestData = getServicesRequestByIdData?.horseLease || getServicesRequestByIdData?.horseLeasing;

    const displayHorses = useMemo(() => {
        let horses = [...(ownerHorsesDropDownData || [])];
        if (leaseRequestData?.horse && !horses.some(h => h.id === leaseRequestData.horseId)) {
            horses.push({
                id: leaseRequestData.horseId,
                registerNumber: leaseRequestData.horseRegistrationNumber || leaseRequestData.horse.registrationNumber,
                name: currentLanguage === 'en' ? leaseRequestData.horseNameEn : leaseRequestData.horseNameAr
            });
        }
        return horses;
    }, [ownerHorsesDropDownData, leaseRequestData, currentLanguage]);

    const horseDetails = useMemo(() => {
        return displayHorses?.find(x => x.id === leaseRequestData?.horseId);
    }, [displayHorses, leaseRequestData?.horseId]);

    const fallbackHorse = leaseRequestData?.horse;

    useEffect(() => {
        if (leaseRequestData && !hasInitialized.current) {
            const lesseeName = currentLanguage === 'en' ? leaseRequestData.lesseeNameEn : leaseRequestData.lesseeNameAr;
            const lesseeLabel = getServicesRequestByIdData?.ownerId ? `${getServicesRequestByIdData.ownerId} - ${lesseeName}` : lesseeName;

            const resetData = {
                [LeaseFieldNames.HORSE_ID]: leaseRequestData.horseId,
                [LeaseFieldNames.LESSEE_ID]: {
                    label: lesseeLabel,
                    value: leaseRequestData.lesseeId,
                    mobile: getServicesRequestByIdData?.ownerPhone || "",
                    idNumber: getServicesRequestByIdData?.idNumber || "",
                    email: getServicesRequestByIdData?.ownerEmail || ""
                },
                [LeaseFieldNames.LEASE_START_DATE]: leaseRequestData.leaseStartDate ? dayjs(leaseRequestData.leaseStartDate).format(DATE_FORMAT_HTML5) : "",
                [LeaseFieldNames.LEASE_END_DATE]: leaseRequestData.leaseEndDate ? dayjs(leaseRequestData.leaseEndDate).format(DATE_FORMAT_HTML5) : "",
                [LeaseFieldNames.CAN_PARTICIPATE_BEAUTY]: leaseRequestData.canParticipateInBeautyChampionships,
                [LeaseFieldNames.CAN_COVER_REGISTER]: leaseRequestData.canCoverAndRegisterFoals,
                [LeaseFieldNames.CAN_REGISTER_OTHER]: leaseRequestData.canRegisterFoalsForOtherOwners,
                [LeaseFieldNames.CAN_IMPORT_EXPORT]: leaseRequestData.canImportAndExport,
                [LeaseFieldNames.IS_MARE_COVERED]: !!leaseRequestData.isMareCovered,
                [LeaseFieldNames.LESSEE_COMMENT]: leaseRequestData.lesseeComment || "",
                [LeaseFieldNames.COMMENT]: getServicesRequestByIdData?.comment || "",
                [LeaseFieldNames.DOC_TYPE_DROP]: leaseRequestData.documentTypeId ?? "",
                [LeaseFieldNames.HORSE_ORIGIN]: horseDetails?.origin || fallbackHorse?.originCountry || "",
                [LeaseFieldNames.HORSE_BREEDER]: horseDetails?.breeder || fallbackHorse?.breeder || "",
                [LeaseFieldNames.HORSE_GENDER_DISPLAY]: horseDetails?.gender ? translate(`web._${horseDetails.gender.toLowerCase()}`, horseDetails.gender) : fallbackHorse?.gender ? translate(`web._${fallbackHorse.gender.toLowerCase()}`, fallbackHorse.gender) : "",
                [LeaseFieldNames.HORSE_GENDER]: horseDetails?.gender || fallbackHorse?.gender || "",
                [LeaseFieldNames.HORSE_COLOR]: horseDetails?.color || fallbackHorse?.horseColor?.nameEn || "",
                [LeaseFieldNames.HORSE_BIRTH_DATE]: horseDetails?.birthDateObject ? formatDateTimeToString(horseDetails.birthDateObject) : fallbackHorse?.birthDateObject ? formatDateTimeToString(fallbackHorse.birthDateObject) : "",
                [LeaseFieldNames.HORSE_SIRE]: horseDetails?.sire || fallbackHorse?.sireNameEn || "",
                [LeaseFieldNames.HORSE_DAM]: horseDetails?.dam || fallbackHorse?.damNameEn || "",
                [LeaseFieldNames.COVERING_CERT_NO]: leaseRequestData?.coveringCertificateNo || getCovCerByHorseIdData?.referenceNumber || getCovCerByHorseIdData?.coveringCertificateNo || getCovCerByHorseIdData?.id || "",
                [LeaseFieldNames.STALLION_NAME]: leaseRequestData?.stallionName || getCovCerByHorseIdData?.stallionName || "",
                [LeaseFieldNames.COVERING_DATE]: leaseRequestData?.coveringDateFormate || getCovCerByHorseIdData?.coveringDateFormate || (getCovCerByHorseIdData?.coveringDate ? dayjs(getCovCerByHorseIdData.coveringDate).format("DD/MM/YYYY") : ""),
                [LeaseFieldNames.ATTACHMENT_FILE]: leaseRequestData.filePath ? [{ name: leaseRequestData.fileName || "Document", preview: leaseRequestData.filePath }] : null
            };

            if (id || leaseRequestData) {
                const termsList = getServiceDefinitionByIdData?.termsList || [];
                termsList.forEach((_, i) => {
                    resetData[`term_${i}`] = true;
                });
            }

            methods.reset(resetData);
            setEventName(getServicesRequestByIdData?.eventName || leaseRequestData.eventName || "");
            hasInitialized.current = true;
        }
    }, [leaseRequestData, currentLanguage, getServicesRequestByIdData, horseDetails, fallbackHorse, getCovCerByHorseIdData, methods, translate, id]);

    // Populate horse details on load if editing
    const watchHorseId = methods.watch(LeaseFieldNames.HORSE_ID);
    useEffect(() => {
        if (watchHorseId && ownerHorsesDropDownData && ownerHorsesDropDownData.length > 0) {
            const horse = ownerHorsesDropDownData.find(item => item.id === watchHorseId);
            if (horse) {
                methods.setValue(LeaseFieldNames.HORSE_GENDER, horse.gender);
                methods.setValue(LeaseFieldNames.HORSE_GENDER_DISPLAY, horse.gender ? translate(`web._${horse.gender.toLowerCase()}`, horse.gender) : "");
                methods.setValue(LeaseFieldNames.HORSE_ORIGIN, horse.origin);
                methods.setValue(LeaseFieldNames.HORSE_BREEDER, horse.breeder);
                methods.setValue(LeaseFieldNames.HORSE_COLOR, horse.color);
                methods.setValue(LeaseFieldNames.HORSE_BIRTH_DATE, formatDateTimeToString(horse.birthDateObject) || horse.birthDate);
                methods.setValue(LeaseFieldNames.HORSE_SIRE, horse.sire);
                methods.setValue(LeaseFieldNames.HORSE_DAM, horse.dam);
            }
        }
    }, [watchHorseId, ownerHorsesDropDownData, methods, translate]);

    // Handle form reactive rules for checkbox dependencies
    useEffect(() => {
        const subscription = methods.watch((value, { name, type }) => {
            if (name === LeaseFieldNames.CAN_COVER_REGISTER && !value[LeaseFieldNames.CAN_COVER_REGISTER]) {
                methods.setValue(LeaseFieldNames.CAN_REGISTER_OTHER, false);
            }
            if (name === LeaseFieldNames.HORSE_GENDER && value[LeaseFieldNames.HORSE_GENDER]?.toLowerCase?.() !== HorseGender.MARE.toLowerCase()) {
                methods.setValue(LeaseFieldNames.CAN_REGISTER_OTHER, false);
            }
        });
        return () => subscription.unsubscribe();
    }, [methods]);

    // Handle Mare Covering Certificate API data mapping
    useEffect(() => {
        if (getCovCerByHorseIdData && (getCovCerByHorseIdData.id || getCovCerByHorseIdData.referenceNumber || getCovCerByHorseIdData.coveringCertificateId)) {
            const certNo = getCovCerByHorseIdData.referenceNumber || getCovCerByHorseIdData.coveringCertificateNo || getCovCerByHorseIdData.id || "";
            const stallion = getCovCerByHorseIdData.stallionName || getCovCerByHorseIdData.stallion || "";
            const covDate = getCovCerByHorseIdData.coveringDateFormate || (getCovCerByHorseIdData.coveringDate ? dayjs(getCovCerByHorseIdData.coveringDate).format("DD/MM/YYYY") : "");

            if (certNo) {
                methods.setValue(LeaseFieldNames.COVERING_CERT_NO, certNo, { shouldValidate: true, shouldDirty: true });
            }
            if (stallion) {
                methods.setValue(LeaseFieldNames.STALLION_NAME, stallion, { shouldValidate: true, shouldDirty: true });
            }
            if (covDate) {
                methods.setValue(LeaseFieldNames.COVERING_DATE, covDate, { shouldValidate: true, shouldDirty: true });
            }
        }
    }, [getCovCerByHorseIdData, methods]);


    // Handle Toaster Messages
    useEffect(() => {
        if (toasterMessage === "save") {
            toast.success(translate(TranslationKeys.DATA_SAVED, "Data Saved Successfully"));
            clearToasterMessage();

            setTimeout(() => {
                const isPaymentComplete = getServicesRequestByIdData?.paymentStatus === PaymentStatus.COMPLETE;
                const hasFees = (getServiceDefinitionByIdData?.serviceFees ?? 0) > 0;
                if (_isApproved.current === true && hasFees && !isPaymentComplete) {
                    PaymentService.redirectToPaymentGateway({
                        entityId: id,
                        paymentType: PaymentTypeMethods.HORSE_LEASING,
                        returnUrl: PaymentRedirectUrls.getEServicesRedirectUrl(id),
                        lang: currentLanguage,
                        title: currentLanguage === "ar" ? getServiceDefinitionByIdData?.nameAr : getServiceDefinitionByIdData?.title,
                        isOnline: _isPayNow.current
                    });
                } else {
                    navigate(PaymentRedirectUrls.getEservicesUrl());
                }
            }, 1500);

        } else if (toasterMessage) {
            toast.error(translate(toasterMessage, toasterMessage));
            clearToasterMessage();
        }
    }, [toasterMessage, dispatch, navigate, translate, id, currentLanguage, getServiceDefinitionByIdData]);

    const promiseOptions = (searchKeyword) =>
        new Promise((resolve) => {
            if (!searchKeyword || searchKeyword.trim().length === 0) {
                resolve([]);
                return;
            }

            if (timeoutId > 0) clearTimeout(timeoutId);
            const _id = setTimeout(async () => {
                try {
                    const data = await UsersService.filterOwnersOrUsers({
                        language: currentLanguage,
                        ownerId: searchKeyword
                    });

                    const options = data.ownersList.map((item) => ({
                        label: `${item.ownerId} - ${item.name}`,
                        value: item.id,
                        mobile: item.ownerPhone || item.phone,
                        email: item.ownerEmail || item.email,
                        idNumber: item.idNumber
                    }));
                    resolve(options);
                } catch (e) {
                    resolve([]);
                }
            }, 800);
            setTimeoutId(_id);
        });

    const onHorseChange = (selected) => {
        const horse = ownerHorsesDropDownData.find(item => item.id === selected?.value);
        if (horse) {
            methods.setValue(LeaseFieldNames.HORSE_GENDER, horse.gender);
            methods.setValue(LeaseFieldNames.HORSE_GENDER_DISPLAY, horse.gender ? translate(`web._${horse.gender.toLowerCase()}`, horse.gender) : "");
            methods.setValue(LeaseFieldNames.HORSE_ORIGIN, horse.origin);
            methods.setValue(LeaseFieldNames.HORSE_BREEDER, horse.breeder);
            methods.setValue(LeaseFieldNames.HORSE_COLOR, horse.color);
            methods.setValue(LeaseFieldNames.HORSE_BIRTH_DATE, horse.birthDateObject ? formatDateTimeToString(horse.birthDateObject) : "");
            methods.setValue(LeaseFieldNames.HORSE_SIRE, horse.sire);
            methods.setValue(LeaseFieldNames.HORSE_DAM, horse.dam);
            methods.setValue(LeaseFieldNames.IS_MARE_COVERED, false);
            methods.setValue(LeaseFieldNames.COVERING_CERT_NO, "");
            methods.setValue(LeaseFieldNames.STALLION_NAME, "");
            methods.setValue(LeaseFieldNames.COVERING_DATE, "");

            if (horse.gender?.toLowerCase?.() === HorseGender.MARE.toLowerCase()) {
                fetchCoveringCertificate(horse.id);
            } else {
                methods.setValue(LeaseFieldNames.CAN_REGISTER_OTHER, false);
            }
        }
    };

    const handleCreateEdit = (status) => {
        return methods.handleSubmit(
            (data) => {
                if (!id) {
                    const termsList = getServiceDefinitionByIdData?.termsList || [];
                    let allTermsChecked = true;
                    for (let i = 0; i < termsList.length; i++) {
                        if (!data[`term_${i}`]) {
                            allTermsChecked = false;
                            methods.setError(`term_${i}`, { type: "manual", message: translate(TranslationKeys.REQUIRED_FIELD, "Required Field") });
                        }
                    }
                    if (!allTermsChecked) {
                        toast.error(translate(TranslationKeys.PLEASE_CHECK_ALL_TERMS, "Please Check All Terms"));
                        return;
                    }
                }

                const isMare = data[LeaseFieldNames.HORSE_GENDER]?.toLowerCase?.() === HorseGender.MARE.toLowerCase();
                const isCovered = isMare && data[LeaseFieldNames.IS_MARE_COVERED];
                const certId = isCovered ? (
                    (leaseRequestData && data[LeaseFieldNames.HORSE_ID] === leaseRequestData.horseId) 
                        ? leaseRequestData?.coveringCertificateId 
                        : (getCovCerByHorseIdData?.id || getCovCerByHorseIdData?.coveringCertificateId || leaseRequestData?.coveringCertificateId || null)
                ) : null;

                const payload = {
                    id: leaseRequestData?.id || "",
                    serviceId: id || "",
                    [LeaseFieldNames.HORSE_ID]: data[LeaseFieldNames.HORSE_ID],
                    ownerId: selectedOwnerId,
                    [LeaseFieldNames.LESSEE_ID]: data[LeaseFieldNames.LESSEE_ID]?.value || data[LeaseFieldNames.LESSEE_ID],
                    [LeaseFieldNames.LEASE_START_DATE]: data[LeaseFieldNames.LEASE_START_DATE],
                    [LeaseFieldNames.LEASE_END_DATE]: data[LeaseFieldNames.LEASE_END_DATE],
                    [LeaseFieldNames.CAN_PARTICIPATE_BEAUTY]: data[LeaseFieldNames.CAN_PARTICIPATE_BEAUTY],
                    [LeaseFieldNames.CAN_COVER_REGISTER]: data[LeaseFieldNames.CAN_COVER_REGISTER],
                    [LeaseFieldNames.CAN_REGISTER_OTHER]: isMare ? data[LeaseFieldNames.CAN_REGISTER_OTHER] : false,
                    [LeaseFieldNames.CAN_IMPORT_EXPORT]: data[LeaseFieldNames.CAN_IMPORT_EXPORT],
                    [LeaseFieldNames.IS_MARE_COVERED]: isMare ? data[LeaseFieldNames.IS_MARE_COVERED] : null,
                    coveringCertificateId: certId,
                    buttonAction: status,
                    status: status,
                    documentTypeId: data[LeaseFieldNames.DOC_TYPE_DROP],
                    [LeaseFieldNames.ATTACHMENT_FILE]: data[LeaseFieldNames.ATTACHMENT_FILE] && data[LeaseFieldNames.ATTACHMENT_FILE].length > 0 && data[LeaseFieldNames.ATTACHMENT_FILE][0].file ? data[LeaseFieldNames.ATTACHMENT_FILE][0].file : null
                };

                dispatch(CreateOrEditHorseLeasethunk(payload));
            },
            (errors) => {
                const errorKeys = Object.keys(errors);
                if (errorKeys.length > 0) {
                    const firstErrorKey = errorKeys[0];
                    const targetEl = document.getElementById(firstErrorKey) || document.querySelector(`[name="${firstErrorKey}"]`);
                    if (targetEl) {
                        targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
                        targetEl.focus?.();
                    }
                }
            }
        )();
    };

    const handleRejectClick = () => {
        const comment = methods.getValues(LeaseFieldNames.LESSEE_COMMENT);
        if (!comment?.trim()) {
            methods.setError(LeaseFieldNames.LESSEE_COMMENT, { type: "manual", message: translate(TranslationKeys.REQUIRED_FIELD, "Required Field") });
            toast.error(translate(TranslationKeys.REJECT_BUYER_COMMENTS, "To Reject The Request Please Enter The Lessee Comments"));
            return;
        } else {
            methods.clearErrors(LeaseFieldNames.LESSEE_COMMENT);
        }
        setOpenPopupReject(true);
    };

    const handleApproveReject = (isApproved, isPayNow = true) => {
        _isApproved.current = isApproved;
        _isPayNow.current = isPayNow;
        const comment = methods.getValues(LeaseFieldNames.LESSEE_COMMENT);
        if (!isApproved && !comment?.trim()) {
            methods.setError(LeaseFieldNames.LESSEE_COMMENT, { type: "manual", message: translate(TranslationKeys.REQUIRED_FIELD, "Required Field") });
            toast.error(translate(TranslationKeys.REJECT_LESSEE_COMMENTS, "To Reject The Request Please Enter The Lessee Comments"));
            return;
        } else {
            methods.clearErrors(LeaseFieldNames.LESSEE_COMMENT);
        }

        const payload = {
            serviceId: id,
            isApproved: isApproved,
            isOnlinePayment: isPayNow,
            comment: comment
        };

        dispatch(ApproveRejectHorseLeasethunk(payload));
    };

    const isReadOnly = action === ServiceActionTypes.VIEW || action === ServiceActionTypes.APPROVE || (id && eventName !== ServiceEventNames.DRAFT && eventName !== ServiceEventNames.SENT_BACK && eventName !== ServiceEventNames.LESSEE_REJECTED && action !== ServiceActionTypes.EDIT);
    const isOwner = !id || (getServicesRequestByIdData && (getServicesRequestByIdData.ownerId == currentUserId || getServicesRequestByIdData.ownerId == selectedOwnerId));
    const isLessee = leaseRequestData && (leaseRequestData.lesseeId === currentUserId || leaseRequestData.lesseeId === localStorage.getItem("currentSubAccountId") || action === ServiceActionTypes.APPROVE);
    const isPaymentComplete = getServicesRequestByIdData?.paymentStatus === PaymentStatus.COMPLETE || getServicesRequestByIdData?.paymentStatus === "Complete";
    const hasFees = (getServiceDefinitionByIdData?.serviceFees ?? 0) > 0;

    return (
        <FormProvider {...methods}>
            <div className={`page-overlay text-center ${isLoading ? "classdisplay" : "classnotdisplay"}`}>
                <div className="loaderDiv"><Loader /></div>
            </div>
            <ToastContainer position="top-right" theme="colored" autoClose={5000} />

            <div className={serviceRequestHeadContainer}>
                <h2 className={serviceRequestTitle}>
                    {currentLanguage === "ar" ? getServiceDefinitionByIdData?.nameAr : getServiceDefinitionByIdData?.title} {leaseRequestData?.referenceNumber ? `- ${leaseRequestData.referenceNumber}` : ""}
                </h2>
                {(!id || action === ServiceActionTypes.EDIT) && (
                    <div className={userDropDown}>
                        <UserCentralDropDown onChange={(newOwnerId) => {
                            setSelectedOwnerId(newOwnerId);
                            methods.setValue(LeaseFieldNames.HORSE_ID, "");
                            methods.setValue(LeaseFieldNames.HORSE_GENDER, "");
                            methods.setValue(LeaseFieldNames.HORSE_GENDER_DISPLAY, "");
                            methods.setValue(LeaseFieldNames.IS_MARE_COVERED, false);
                        }} />
                    </div>
                )}
            </div>

            <HorseLeasingHorseSection
                isReadOnly={isReadOnly}
                ownerHorsesDropDownData={displayHorses}
                onHorseChange={onHorseChange}
            />

            <HorseLeasingCoveringSection
                isReadOnly={isReadOnly}
            />

            <HorseLeasingLesseeSection
                isReadOnly={isReadOnly || (id && eventName !== ServiceEventNames.DRAFT)}
                promiseOptions={promiseOptions}
            />

            <HorseLeasingLeaseDetailsSection
                isReadOnly={isReadOnly}
            />

            <HorseLeasingDocumentSection
                getDocumentTypesByServiceIdData={getDocumentTypesByServiceIdData}
                leaseRequestData={leaseRequestData}
                currentLanguage={currentLanguage}
                isReadOnly={isReadOnly}
            />
            <HorseLeasingCommentsSection
                isLessee={isLessee}
                action={action}
            />


            <HorseLeasingTermsSection
                termsList={getServiceDefinitionByIdData?.termsList}
                isReadOnly={isReadOnly || !!id}
            />

            <HorseLeasingActionButtons
                id={id}
                action={action}
                eventName={eventName}
                isLessee={isLessee}
                hasFees={hasFees}
                canParticipateWithoutPayment={canParticipateWithoutPayment}
                isPaymentComplete={isPaymentComplete}
                selectedOwnerId={selectedOwnerId}
                handleCreateEdit={handleCreateEdit}
                handleRejectClick={handleRejectClick}
                handleApproveReject={handleApproveReject}
            />

            <CustomPopUp
                title={translate(TranslationKeys.CONFIRM_MSG_REJECT_REQUEST, "Are you sure to reject request?")}
                open={openPopupReject}
                onClose={() => setOpenPopupReject(false)}
                btnText={translate(TranslationKeys.REJECT, "Reject")}
                contentStyle={{ maxWidth: "450px" }}
                onClick={() => {
                    setOpenPopupReject(false);
                    handleApproveReject(false);
                }}
            />
        </FormProvider>
    );
}
